import API from 'utils/api';
import { onError, onSuccess } from 'utils/handlers';
import formatValues, { hasFiles } from 'utils/formatValues';

// URL base para las peticiones de archivos de infracciones
export const baseURI = '/recaudacion/archivos-de-infracciones/';
const files = ['archivo'];
const headers = { 'Content-Type': 'multipart/form-data' };

/**
 * Obtiene una lista de archivos de infracciones.
 * @async
 * @function
 * @param {Object} params - Parámetros de consulta para filtrar los archivos.
 * @returns {Promise<Array>} - Retorna una lista de archivos de infracciones o un arreglo
 * vacío en caso de error.
 */
export const getArchivosDeInfracciones = async (params) => {
  try {
    const response = await API.get(baseURI, { params });
    return response.data.map((archivo) => ({
      ...archivo,
      uid: archivo.id,
      name: archivo.descripcion,
      status: 'done',
      url: archivo.archivo,
    }));
  } catch (error) {
    onError(error);
    return [];
  }
};

/**
 * Obtiene un archivo de infracciones por su ID.
 * @async
 * @function
 * @param {string} id - Identificador del archivo de infracciones.
 * @returns {Promise<Object|null>} - Retorna el archivo de infracciones o null en caso de error.
 */
export const getArchivoDeInfracciones = async (id) => {
  try {
    const response = await API.get(`${baseURI}${id}`);
    return response.data;
  } catch (error) {
    onError(error);
    return null;
  }
};

/**
 * Crea un nuevo archivo de infracciones.
 * @async
 * @function
 * @param {Object} data - Datos del archivo de infracciones a crear.
 * @returns {Promise<Object|boolean>} - Retorna el archivo creado o false en caso de error.
 */
export const postArchivoDeInfracciones = async (data) => {
  try {
    const config = hasFiles(data, files) ? { headers } : {};
    const values = formatValues(data, { formData: true });
    const response = await API.post(baseURI, values, config);
    return response.data;
  } catch (error) {
    onError(error);
    return false;
  }
};

/**
 * Actualiza un archivo de infracciones existente.
 * @async
 * @function
 * @param {string} id - Identificador del archivo de infracciones a actualizar.
 * @param {Object} data - Datos del archivo de infracciones a actualizar.
 * @returns {Promise<Object|boolean>} - Retorna el archivo actualizado o false en caso de error.
 */
export const patchArchivoDeInfracciones = async (id, data) => {
  try {
    const config = hasFiles(data, files) ? { headers } : {};
    const values = formatValues(data, { formData: true });
    const response = await API.patch(`${baseURI}${id}`, values, config);
    return response.data;
  } catch (error) {
    onError(error);
    return false;
  }
};

/**
 * Elimina un archivo de infracciones por su ID.
 * @async
 * @function
 * @param {number} id - Identificador del archivo de infracciones a eliminar.
 * @returns {Promise<boolean>} - Retorna true si la eliminación fue exitosa, o false en caso
 * de error.
 */
export const deleteArchivoDeInfracciones = async (id) => {
  try {
    const response = await API.delete(`${baseURI}${id}`);
    onSuccess(response, 'Archivo de infracciones eliminado exitosamente');
    return true;
  } catch (error) {
    onError(error);
    return false;
  }
};
